import * as React from "react";
import { TabBar } from "../";
import { useSelect } from "@hubper/theme";

type SubPageContextProps = {
  activeSubPage: SubPageProps | undefined;
  registerSubPage: (subPage: SubPageProps, active: boolean | undefined) => void;
  unregisterSubPage: (subPage: SubPageProps) => void;
};

const SubPageContext = React.createContext<SubPageContextProps>(
  {} as SubPageContextProps
);

type SubPageProps = {
  children?: any;
  value: string; // Erik: wat betekent value?
  display: string;
  active?: boolean;
};

const SubPage = ({ children, value, display, active }: SubPageProps) => {
  const {
    registerSubPage,
    activeSubPage,
    unregisterSubPage
  } = React.useContext(SubPageContext);

  React.useEffect(() => {
    registerSubPage({ value, display }, active);

    return () => {
      unregisterSubPage({ value, display });
    };
  }, []);

  return activeSubPage && activeSubPage.value === value
    ? children || null
    : null;
};

type Props = {
  children?: any;
  onChange?: (subPage: SubPageProps) => void;
};

const SubPageContainer = React.forwardRef(function SubPageContainer(
  { children, onChange }: Props,
  ref: any
) {
  const { space } = useSelect();

  const [activeSubPage, setActiveSubPage] = React.useState<
    SubPageProps | undefined
  >(undefined);

  const [items, setItems] = React.useState<SubPageProps[]>([]);

  React.useEffect(() => {
    if (onChange && activeSubPage) {
      onChange(activeSubPage);
    }
  }, [activeSubPage]);

  const unregisterSubPage = (subPage: SubPageProps) => {
    const isActive = activeSubPage && activeSubPage.value === subPage.value;
    const newItems = items.filter(item => item.value !== subPage.value);
    setItems(newItems);
    if (isActive) {
      setActiveSubPage(newItems[0]);
    }
  };

  const registerSubPage = (
    subPage: SubPageProps,
    active: boolean | undefined
  ) => {
    setItems(items => items.concat(subPage));
    if (active) {
      setActiveSubPage(subPage);
    }
  };

  return (
    <>
      {items && items.length > 0 && (
        <TabBar
          ref={ref}
          items={items}
          onChange={id => setActiveSubPage(items.find(x => x.value === id))}
          selectedItem={activeSubPage ? activeSubPage.value : ""}
          style={{ marginBottom: space(4) }}
        />
      )}
      <SubPageContext.Provider
        value={{ registerSubPage, activeSubPage, unregisterSubPage }}
      >
        {children}
      </SubPageContext.Provider>
    </>
  );
}) as React.ComponentType<Props> & {
  SubPage?: React.ComponentType<SubPageProps>;
};

SubPageContainer.SubPage = SubPage;

export default SubPageContainer;
